'use strict';

import { Helper } from '../helpers/Helper.js';
import { Toast } from '../helpers/Toast.js';
import { syncChoicesSelectValue } from '../helpers/ChoicesSelectValue.js';

/**
 * Team / participant campaign edit form: file upload triggers, additional images removal, optional session check.
 */
export class TeamCampaignEdit {
	static #dirtyBaselines = new WeakMap();
	static #pendingAdditionalFiles = new WeakMap();
	static #uploadClickBound = false;
	static #predefinedBannerClickBound = false;
	static #imagePreviewBound = false;
	static #activePickerInput = null;
	static #ignoreUploadClicksUntil = 0;
	/**
	 * POST to admin-ajax via Helper.request; shows Toast on failure and optional success.
	 *
	 * @param {string}                                             url     admin-ajax URL
	 * @param {Record<string, string|number|boolean>}              data    must include `action`
	 * @param {{ successMessage?: string, errorMessage?: string }} options
	 * @return {Promise<object|null>} parsed JSON on success, null after error toast
	 */
	static async requestWithToast( url, data, options = {} ) {
		const { successMessage, errorMessage } = options;
		const defaultError =
			typeof wp !== 'undefined' && wp.i18n
				? wp.i18n.__(
						'Something went wrong. Please try again.',
						'levcharity'
				  )
				: 'Something went wrong. Please try again.';
		try {
			const res = await Helper.request( url, data );
			if ( successMessage ) {
				Toast.success( successMessage );
			}
			return res;
		} catch {
			Toast.error( errorMessage || defaultError );
			return null;
		}
	}

	constructor() {
		TeamCampaignEdit.#bindUploadButtonsOnce();
		TeamCampaignEdit.#bindPredefinedBannerOnce();
		TeamCampaignEdit.#bindImagePreviewsOnce();

		document
			.querySelectorAll(
				'.levcharity-team-campaign-edit, .levcharity-p2p-campaign-edit'
			)
			.forEach( ( root ) => {
				if ( root.dataset.levcharityTeamEditBound === '1' ) {
					return;
				}
				root.dataset.levcharityTeamEditBound = '1';
				this.#bindBannerPreview( root );
				this.#bindAdditionalImagesPreview( root );
				this.#bindRemoveAdditional( root );
				this.#applyTshirtVisibility( root );
				this.#initializeSignupCustomFieldDefaults( root );
				this.#bindAjaxFormSubmit( root );
				this.#verifyEditContext( root );
			} );
	}

	#syncTinyMceBeforeSubmit() {
		if ( typeof window.tinymce !== 'undefined' ) {
			window.tinymce.triggerSave();
		}
	}

	#syncChainationEventFieldsBeforeSubmit( form ) {
		form.querySelectorAll( 'select[name="chaination_event_city"]' ).forEach(
			( select ) => syncChoicesSelectValue( select )
		);
	}

	#t( msg ) {
		return typeof wp !== 'undefined' && wp.i18n
			? wp.i18n.__( msg, 'levcharity' )
			: msg;
	}

	#getMainCampaignImageInput( form ) {
		return form.querySelector(
			'input.levcharity-campaign-main-image[type="file"]'
		);
	}

	#getAdditionalCampaignImagesInput( rootOrForm ) {
		return rootOrForm.querySelector(
			'#additional_image, #levcharity_p2p_photos, input.additional-image-edit[type="file"]'
		);
	}

	#getCampaignMessageHtml( form ) {
		const el = form.querySelector( '#campaign_message' );
		if ( ! el ) {
			return '';
		}
		if ( typeof window.tinymce !== 'undefined' ) {
			const ed = window.tinymce.get( 'campaign_message' );
			if ( ed && ! ed.isHidden() ) {
				return ed.getContent();
			}
		}
		return el.value || '';
	}

	#getCampaignMessagePlainText( form ) {
		const el = form.querySelector( '#campaign_message' );
		if ( ! el ) {
			return '';
		}
		if ( typeof window.tinymce !== 'undefined' ) {
			const ed = window.tinymce.get( 'campaign_message' );
			if ( ed && ! ed.isHidden() ) {
				return ed
					.getContent( { format: 'text' } )
					.replace( /\u00a0/g, ' ' )
					.trim();
			}
		}
		const html = el.value || '';
		const tmp = document.createElement( 'div' );
		tmp.innerHTML = html;
		return ( tmp.textContent || '' ).trim();
	}

	#isSvgFile( file ) {
		if ( ! file ) {
			return false;
		}
		const name = ( file.name || '' ).toLowerCase();
		if ( name.endsWith( '.svg' ) ) {
			return true;
		}
		const type = ( file.type || '' ).toLowerCase();
		return type === 'image/svg+xml' || type === 'image/svg';
	}

	#validateCampaignImageFiles( form ) {
		const inputs = [
			this.#getMainCampaignImageInput( form ),
			form.querySelector( 'input[name="banner_image"][type="file"]' ),
			form.querySelector( 'input[name="featured_image"][type="file"]' ),
			this.#getAdditionalCampaignImagesInput( form ),
		].filter( Boolean );

		for ( const input of inputs ) {
			if ( ! input.files?.length ) {
				continue;
			}
			for ( const file of input.files ) {
				if ( this.#isSvgFile( file ) ) {
					return this.#t( 'SVG format is not supported.' );
				}
			}
		}
		return '';
	}

	#isValidOptionalVideoUrl( value ) {
		const t = ( value || '' ).trim();
		if ( ! t ) {
			return true;
		}
		try {
			const u = new URL( t.startsWith( '//' ) ? `https:${ t }` : t );
			return u.protocol === 'http:' || u.protocol === 'https:';
		} catch {
			return false;
		}
	}

	/**
	 * @return {string} error message or empty string if valid
	 */
	#validateTeamCampaignForm( root, form ) {
		const isTeam = root.dataset.isTeam === '1';
		const requireGoal = root.dataset.requireFundraisingGoal === '1';
		const hadBanner = root.dataset.hasBanner === '1';

		const titleInput = form.querySelector( '[name="campaign_title"]' );
		if ( titleInput && ! String( titleInput.value || '' ).trim() ) {
			return this.#t( 'Please enter a campaign title.' );
		}

		const subtitleInput = form.querySelector( '[name="campaign_subtitle"]' );
		if ( subtitleInput && ! String( subtitleInput.value || '' ).trim() ) {
			return this.#t( 'Please enter a subtitle.' );
		}

		if ( requireGoal ) {
			const goalInput = form.querySelector( '[name="campaign_goal_amount"]' );
			const raw = goalInput ? String( goalInput.value || '' ).trim() : '';
			const n = parseFloat( raw.replace( ',', '.' ) );
			if ( raw === '' || Number.isNaN( n ) || n <= 0 ) {
				return this.#t( 'Please enter a valid fundraising goal.' );
			}
		}

		if ( isTeam ) {
			const teamName = form.querySelector( '[name="team_name"]' );
			if ( teamName && ! String( teamName.value || '' ).trim() ) {
				return this.#t( 'Please enter a team name.' );
			}
		}

		const messageText = this.#getCampaignMessagePlainText( form );
		if ( ! messageText ) {
			return this.#t( 'Please enter a welcome message.' );
		}

		// The campaign may hide the image upload, in which case the fundraiser has no
		// way to provide one and a default image is used. A missing file input means
		// the same thing even if data-require-banner was not rendered.
		const mainImageInput = this.#getMainCampaignImageInput( form );
		const requireBanner =
			root.dataset.requireBanner !== '0' && !! mainImageInput;
		if ( requireBanner ) {
			const hasNewMainImage =
				mainImageInput.files && mainImageInput.files.length > 0;
			const previewImg = root.querySelector(
				'.levcharity-main-campaign-preview-host img'
			);
			const hasVisibleBanner =
				hasNewMainImage ||
				hadBanner ||
				( previewImg &&
					previewImg.getAttribute( 'src' ) &&
					previewImg.getAttribute( 'src' ).trim() !== '' );
			if ( ! hasVisibleBanner ) {
				return this.#t( 'Please upload a campaign image.' );
			}
		}

		const svgError = this.#validateCampaignImageFiles( form );
		if ( svgError ) {
			return svgError;
		}

		const videoInput = form.querySelector( '[name="video_url"]' );
		if (
			videoInput &&
			! this.#isValidOptionalVideoUrl( videoInput.value )
		) {
			return this.#t( 'Please enter a valid video URL.' );
		}

		const slugInput = form.querySelector( '[name="campaign_url"]' );
		if ( slugInput && ! String( slugInput.value || '' ).trim() ) {
			return this.#t( 'Please enter a URL slug for your page.' );
		}

		const scfError = this.#validateSignupCustomFields( form );
		if ( scfError ) {
			return scfError;
		}

		return '';
	}

	#isSignupFieldWrapperVisible( wrapper ) {
		if ( ! wrapper ) {
			return false;
		}
		if ( wrapper.closest( '[hidden]' ) ) {
			return false;
		}
		const style = window.getComputedStyle( wrapper );
		return style.display !== 'none' && style.visibility !== 'hidden';
	}

	#getSignupFieldLabel( wrapper ) {
		const labelEl = wrapper.querySelector( '.group-title' );
		const label = labelEl?.textContent?.replace( /\*/g, '' ).trim() || '';
		return label;
	}

	#formatRequiredFieldMessage( fieldLabel ) {
		if (
			typeof wp !== 'undefined' &&
			wp.i18n &&
			typeof wp.i18n.sprintf === 'function'
		) {
			return wp.i18n.sprintf(
				wp.i18n.__( 'Please enter the field "%s."', 'levcharity' ),
				fieldLabel
			);
		}

		return `${this.#t( 'Please enter the field' )} "${fieldLabel}".`;
	}

	#initializeSignupCustomFieldDefaults( root ) {
		root
			.querySelectorAll(
				'.levcharity-myaccount-signup-custom-fields select.levcharity-custom-field'
			)
			.forEach( ( select ) => {
				this.#getRequiredCustomFieldValue( select );
			} );
	}

	#getRequiredCustomFieldValue( control ) {
		if ( ! control || control.tagName !== 'SELECT' || control.value ) {
			return control?.value || '';
		}

		if ( ! control.classList.contains( 'levcharity-custom-field' ) ) {
			return control.value || '';
		}

		const firstOption = [ ...control.options ].find(
			( option ) => ! option.disabled && option.value !== ''
		);
		if ( ! firstOption ) {
			return control.value || '';
		}

		control.value = firstOption.value;
		syncChoicesSelectValue( control );

		return control.value || '';
	}

	/**
	 * @param {HTMLFormElement} form
	 * @return {string}
	 */
	#validateSignupCustomFields( form ) {
		const scfRoot = form.querySelector( '.levcharity-myaccount-signup-custom-fields' );
		if ( ! scfRoot ) {
			return '';
		}

		const wrappers = scfRoot.querySelectorAll( '.levcharity_form__field' );
		for ( const wrapper of wrappers ) {
			if ( ! this.#isSignupFieldWrapperVisible( wrapper ) ) {
				continue;
			}

			const requiredControl = wrapper.querySelector( '[required]' );
			if ( ! requiredControl ) {
				continue;
			}

			const label = this.#getSignupFieldLabel( wrapper );
			const fieldLabel = label || this.#t( 'This field' );

			if ( requiredControl.type === 'radio' ) {
				const checked = wrapper.querySelector( 'input[type="radio"]:checked' );
				if ( ! checked ) {
					return this.#formatRequiredFieldMessage( fieldLabel );
				}
				continue;
			}

			if ( requiredControl.type === 'checkbox' ) {
				if ( ! requiredControl.checked ) {
					return this.#formatRequiredFieldMessage( fieldLabel );
				}
				continue;
			}

			if ( requiredControl.tagName === 'SELECT' ) {
				if (
					! String(
						this.#getRequiredCustomFieldValue( requiredControl ) || ''
					).trim()
				) {
					return this.#formatRequiredFieldMessage( fieldLabel );
				}
				continue;
			}

			if ( requiredControl.type === 'file' ) {
				// Existing uploads are validated server-side; empty file input may still be valid on edit.
				continue;
			}

			if (
				! String(
					this.#getRequiredCustomFieldValue( requiredControl ) || ''
				).trim()
			) {
				return this.#formatRequiredFieldMessage( fieldLabel );
			}
		}

		return '';
	}

	#serializeTeamCampaignFormState( root, form ) {
		this.#syncTinyMceBeforeSubmit();
		const text = {};
		const trackedNames = [
			'campaign_title',
			'campaign_subtitle',
			'campaign_goal_amount',
			'team_name',
			'video_url',
			'campaign_url',
			'removed_images',
			'teams_goal',
			'campaign_id',
			'qr_image',
		];
		for ( const name of trackedNames ) {
			const el = form.querySelector( `[name="${ name }"]` );
			if ( el ) {
				text[ name ] = el.value;
			}
		}
		text.campaign_message = this.#getCampaignMessageHtml( form );
		const bannerNamed = form.querySelector(
			'input[name="banner_image"][type="file"]'
		);
		const featuredNamed = form.querySelector(
			'input[name="featured_image"][type="file"]'
		);
		const additional = this.#getAdditionalCampaignImagesInput( form );
		const files = {
			banner_image: bannerNamed?.files?.length ?? 0,
			featured_image: featuredNamed?.files?.length ?? 0,
			additional_image: additional?.files?.length ?? 0,
		};
		const existingImages = [];
		form.querySelectorAll( 'input[name="existing_images[]"]' ).forEach( ( el ) => {
			existingImages.push( el.value );
		} );
		existingImages.sort();
		return { text, files, existing_images: existingImages };
	}

	#waitCampaignMessageEditorThen( form, callback, attempt = 0 ) {
		const maxAttempts = 80;
		const ed =
			typeof window.tinymce !== 'undefined'
				? window.tinymce.get( 'campaign_message' )
				: null;
		if ( ed ) {
			if ( ed.initialized ) {
				callback();
				return;
			}
			if ( typeof ed.once === 'function' ) {
				ed.once( 'init', callback );
			} else {
				const onInit = () => {
					ed.off( 'init', onInit );
					callback();
				};
				ed.on( 'init', onInit );
			}
			return;
		}
		if ( attempt >= maxAttempts ) {
			callback();
			return;
		}
		window.setTimeout(
			() =>
				this.#waitCampaignMessageEditorThen(
					form,
					callback,
					attempt + 1
				),
			64
		);
	}

	#applySubmitDisabledFromDirty( root, form, submitBtn ) {
		const baseline = TeamCampaignEdit.#dirtyBaselines.get( form );
		if ( ! baseline || baseline.value === null ) {
			submitBtn.disabled = true;
			return;
		}
		const now = this.#serializeTeamCampaignFormState( root, form );
		submitBtn.disabled =
			JSON.stringify( now ) === JSON.stringify( baseline.value );
	}

	#bindSubmitDirtyState( root, form ) {
		const submitBtn = form.querySelector( 'button[type="submit"]' );
		if ( ! submitBtn ) {
			return;
		}
		const baseline = { value: null };
		TeamCampaignEdit.#dirtyBaselines.set( form, baseline );
		submitBtn.disabled = true;

		const onChange = () => {
			this.#applySubmitDisabledFromDirty( root, form, submitBtn );
		};

		form.addEventListener( 'input', onChange, true );
		form.addEventListener( 'change', onChange, true );

		const hookMessageEditor = ( ed ) => {
			if ( ed._levcharityTeamCampaignDirtyHook ) {
				return;
			}
			ed._levcharityTeamCampaignDirtyHook = true;
			ed.on( 'Change Undo Redo KeyUp SetContent ExecCommand', onChange );
		};

		if ( typeof window.tinymce !== 'undefined' ) {
			window.tinymce.on( 'AddEditor', ( ev ) => {
				if ( ev.editor?.id === 'campaign_message' ) {
					ev.editor.on( 'init', () => {
						hookMessageEditor( ev.editor );
						onChange();
					} );
				}
			} );
		}

		this.#waitCampaignMessageEditorThen( form, () => {
			this.#syncTinyMceBeforeSubmit();
			const ed =
				typeof window.tinymce !== 'undefined'
					? window.tinymce.get( 'campaign_message' )
					: null;
			if ( ed ) {
				hookMessageEditor( ed );
			}
			baseline.value = this.#serializeTeamCampaignFormState( root, form );
			onChange();
		} );
	}

	#resetDirtyBaselineAfterSave( root, form ) {
		const baseline = TeamCampaignEdit.#dirtyBaselines.get( form );
		const mainImg = this.#getMainCampaignImageInput( form );
		const additional = this.#getAdditionalCampaignImagesInput( form );
		if ( mainImg ) {
			mainImg.value = '';
		}
		if ( additional ) {
			additional.value = '';
			TeamCampaignEdit.#pendingAdditionalFiles.delete( additional );
			this.#renderPendingAdditionalPreviews( root, additional );
		}
		const removedInput = root.querySelector( '#removedImagesInput' );
		if ( removedInput ) {
			removedInput.value = '';
		}
		this.#syncTinyMceBeforeSubmit();
		if ( baseline ) {
			baseline.value = this.#serializeTeamCampaignFormState( root, form );
		}
		const submitBtn = form.querySelector( 'button[type="submit"]' );
		if ( submitBtn ) {
			submitBtn.disabled = true;
		}
	}

	#bindAjaxFormSubmit( root ) {
		const form = root.querySelector( 'form[enctype="multipart/form-data"]' );
		if ( ! form ) {
			return;
		}

		const formAction = String(
			form.querySelector( 'input[name="action"]' )?.value || ''
		);
		const canUseAjaxSave =
			formAction === 'levcharity_myaccount_update_team_campaign';
		if ( ! canUseAjaxSave ) {
			this.#bindNativeP2PSubmitCompat( root, form );
			return;
		}

		this.#bindSubmitDirtyState( root, form );
		const ajaxUrl =
			typeof window !== 'undefined' &&
			window.admin_ajax &&
			window.admin_ajax[ 0 ]
				? window.admin_ajax[ 0 ]
				: '';
		if ( ! ajaxUrl ) {
			return;
		}
		const saving =
			typeof wp !== 'undefined' && wp.i18n
				? wp.i18n.__( 'Saving…', 'levcharity' )
				: 'Saving…';
		const defaultError =
			typeof wp !== 'undefined' && wp.i18n
				? wp.i18n.__(
						'Something went wrong. Please try again.',
						'levcharity'
				  )
				: 'Something went wrong. Please try again.';
		form.addEventListener( 'submit', async ( e ) => {
			e.preventDefault();
			TeamCampaignEdit.releaseHiddenTShirtRequired( form );
			this.#syncTinyMceBeforeSubmit();
			this.#syncChainationEventFieldsBeforeSubmit( form );
			const validationError = this.#validateTeamCampaignForm( root, form );
			if ( validationError ) {
				Toast.error( validationError );
				return;
			}
			const submitBtn = form.querySelector( 'button[type="submit"]' );
			const prevText = submitBtn ? submitBtn.textContent : '';
			if ( submitBtn ) {
				submitBtn.disabled = true;
				submitBtn.textContent = saving;
			}
			const fd = new FormData( form );
			fd.set( 'levcharity_ajax_team_campaign_save', '1' );
			try {
				const res = await fetch( ajaxUrl, {
					method: 'POST',
					body: fd,
					credentials: 'same-origin',
				} );
				const data = await res.json().catch( () => null );
				if ( ! data || typeof data.success === 'undefined' ) {
					Toast.error( defaultError );
					return;
				}
				if ( data.success ) {
					Toast.success(
						data.message ||
							( typeof wp !== 'undefined' && wp.i18n
								? wp.i18n.__(
										'Campaign updated successfully.',
										'levcharity'
								  )
								: 'Campaign updated successfully.' )
					);
					if ( Array.isArray( data.additional_images ) ) {
						this.#applySavedAdditionalImages(
							root,
							data.additional_images
						);
					}
					this.#resetDirtyBaselineAfterSave( root, form );
				} else {
					Toast.error( data.message || defaultError );
				}
			} catch {
				Toast.error( defaultError );
			} finally {
				if ( submitBtn ) {
					submitBtn.textContent = prevText;
					this.#applySubmitDisabledFromDirty( root, form, submitBtn );
				}
			}
		} );
	}

	#replaceCompatInput( form, name, files, multiple = false ) {
		form
			.querySelectorAll( `input[data-levcharity-compat="${ name }"]` )
			.forEach( ( el ) => el.remove() );
		if ( ! files.length ) {
			return;
		}
		const compat = document.createElement( 'input' );
		compat.type = 'file';
		compat.name = name;
		compat.hidden = true;
		compat.style.display = 'none';
		compat.setAttribute( 'data-levcharity-compat', name );
		if ( multiple ) {
			compat.multiple = true;
		}
		const dt = new DataTransfer();
		files.forEach( ( file ) => dt.items.add( file ) );
		compat.files = dt.files;
		form.appendChild( compat );
	}

	#bindNativeP2PSubmitCompat( root, form ) {
		const formAction = String(
			form.querySelector( 'input[name="action"]' )?.value || ''
		);
		if ( formAction !== 'levcharity_update_my_p2p_campaign' ) {
			return;
		}

		form.addEventListener( 'submit', () => {
			const mainImageInput = this.#getMainCampaignImageInput( form );
			const mainFiles = mainImageInput?.files
				? Array.from( mainImageInput.files )
				: [];

			// Backward compatibility: different modules can read different file keys.
			this.#replaceCompatInput( form, 'banner_image', mainFiles );
			this.#replaceCompatInput( form, 'campaign_image', mainFiles );
		} );
	}

	/**
	 * Opt-in preview for single file fields: a field marked with
	 * `data-levcharity-image-preview` renders the picked file into the
	 * `.levcharity-image-preview-host` of its own form section.
	 */
	static #bindImagePreviewsOnce() {
		if ( TeamCampaignEdit.#imagePreviewBound ) {
			return;
		}
		TeamCampaignEdit.#imagePreviewBound = true;

		document.addEventListener( 'change', ( e ) => {
			const input = e.target;
			if (
				! input?.matches?.(
					'input[type="file"][data-levcharity-image-preview]'
				)
			) {
				return;
			}

			const file = input.files?.[ 0 ];
			if ( ! file || ! file.type.startsWith( 'image/' ) ) {
				return;
			}

			const section =
				input.closest( '.form-group-wrapper' ) ||
				input.closest( '.form-group' );
			const host = section?.querySelector(
				'.levcharity-image-preview-host'
			);
			if ( ! host ) {
				return;
			}

			const wrapper = host.closest( '.form-group' );
			if ( wrapper ) {
				wrapper.hidden = false;
			}

			const blobUrl = URL.createObjectURL( file );
			let img = host.querySelector( 'img' );
			if ( img ) {
				if ( img.dataset.blobUrl ) {
					URL.revokeObjectURL( img.dataset.blobUrl );
				}
				img.removeAttribute( 'srcset' );
				img.removeAttribute( 'sizes' );
			} else {
				img = document.createElement( 'img' );
				img.alt = '';
				host.appendChild( img );
			}
			img.dataset.blobUrl = blobUrl;
			img.src = blobUrl;

			const nameEl = section.querySelector(
				'.levcharity-image-preview-name'
			);
			if ( nameEl ) {
				nameEl.textContent = file.name;
			}
		} );
	}

	/**
	 * Predefined banner pickers replace the upload field on some themes: keep the
	 * hidden value, the selected state and the form dirty check in sync.
	 */
	static #bindPredefinedBannerOnce() {
		if ( TeamCampaignEdit.#predefinedBannerClickBound ) {
			return;
		}
		TeamCampaignEdit.#predefinedBannerClickBound = true;

		document.addEventListener( 'click', ( e ) => {
			const option = e.target.closest( '.predefined-banner-option' );
			if ( ! option ) {
				return;
			}

			e.preventDefault();

			const container = option.closest( '.predefined-image-container' );
			const scope =
				option.closest( '.banner-image-edit-container' ) ||
				option.closest( '.form-group' ) ||
				container?.parentElement;
			const hidden = scope?.querySelector( '.predefined-banner-value' );
			const bannerId = option.dataset.bannerId || '';

			if ( hidden && bannerId && hidden.value !== bannerId ) {
				hidden.value = bannerId;
				hidden.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			}

			container
				?.querySelectorAll( '.predefined-banner-option' )
				.forEach( ( element ) => {
					const isSelected = element === option;
					element.classList.toggle( 'is-selected', isSelected );
					element.setAttribute(
						'aria-pressed',
						isSelected ? 'true' : 'false'
					);
				} );
		} );
	}

	static #bindUploadButtonsOnce() {
		if ( TeamCampaignEdit.#uploadClickBound ) {
			return;
		}
		TeamCampaignEdit.#uploadClickBound = true;

		document.addEventListener( 'click', ( e ) => {
			if ( Date.now() < TeamCampaignEdit.#ignoreUploadClicksUntil ) {
				return;
			}

			const btn = e.target.closest(
				'.levcharity-upload-image-button, .upload-image-button-gamification'
			);
			if ( ! btn ) {
				return;
			}

			// Themes may replace the plugin My Account shell with their own
			// (WooCommerce default or custom), so accept any of those wrappers.
			if (
				! btn.closest(
					'#levcharity_my_account, .levcharity_my_account_content, .woocommerce-MyAccount-content'
				)
			) {
				return;
			}

			if (
				! btn.closest(
					'.levcharity-team-campaign-edit, .campaign-details.team-campaign'
				)
			) {
				return;
			}

			e.preventDefault();

			const section =
				btn.closest( '.form-group-wrapper' ) ||
				btn.closest( '.form-group' );
			const fileInput = section?.querySelector( 'input[type="file"]' );
			if ( ! fileInput || fileInput.disabled ) {
				return;
			}

			if ( TeamCampaignEdit.#activePickerInput === fileInput ) {
				return;
			}

			TeamCampaignEdit.#activePickerInput = fileInput;

			const onPickerClosed = () => {
				TeamCampaignEdit.#activePickerInput = null;
				TeamCampaignEdit.#ignoreUploadClicksUntil = Date.now() + 500;
			};

			fileInput.addEventListener( 'cancel', onPickerClosed, { once: true } );
			fileInput.addEventListener( 'change', onPickerClosed, { once: true } );

			fileInput.click();
		} );
	}

	#bindBannerPreview( root ) {
		const mainInput = root.querySelector(
			'input.levcharity-campaign-main-image[type="file"]'
		);
		if ( ! mainInput ) {
			return;
		}
		const previewHost = root.querySelector(
			'.levcharity-main-campaign-preview-host'
		);
		if ( ! previewHost ) {
			return;
		}
		if ( mainInput.dataset.levcharityBannerPreviewBound === '1' ) {
			return;
		}
		mainInput.dataset.levcharityBannerPreviewBound = '1';
		mainInput.addEventListener( 'change', () => {
			const previewImages = previewHost.querySelectorAll( 'img' );
			if ( previewImages.length > 1 ) {
				previewImages.forEach( ( element, index ) => {
					if ( index > 0 ) {
						element.remove();
					}
				} );
			}
			const file = mainInput.files && mainInput.files[ 0 ];
			if ( ! file || ! file.type.startsWith( 'image/' ) ) {
				return;
			}
			const url = URL.createObjectURL( file );
			const wrapper = root.querySelector(
				'.levcharity-campaign-image-wrapper, .gamification-campaign-image-wrapper'
			);
			if ( wrapper ) {
				wrapper.classList.add( 'has-featured-image' );
				wrapper.hidden = false;
				const nameEl = wrapper.querySelector( '.bannered_image_name' );
				const metaEl = wrapper.querySelector( '.bannered_image_metadata' );
				if ( nameEl ) {
					nameEl.textContent = file.name;
				}
				if ( metaEl ) {
					metaEl.textContent = '';
					metaEl.hidden = true;
				}
			}
			let img = previewHost.querySelector( 'img' );
			if ( img && img.src.startsWith( 'blob:' ) ) {
				URL.revokeObjectURL( img.src );
			}
			if ( img ) {
				img.classList.add( 'levcharity-main-campaign-preview-img' );
				img.removeAttribute( 'srcset' );
				img.removeAttribute( 'sizes' );
				img.src = url;
			} else {
				img = document.createElement( 'img' );
				img.className = 'levcharity-main-campaign-preview-img';
				img.src = url;
				img.alt = '';
				previewHost.appendChild( img );
			}
		} );
	}

	#renderPendingAdditionalPreviews( root, input ) {
		const container = root.querySelector(
			'.levcharity-additional-images-preview-host'
		);
		if ( ! container || ! input ) {
			return;
		}
		container
			.querySelectorAll( '.levcharity-pending-additional' )
			.forEach( ( wrap ) => {
				const im = wrap.querySelector( 'img' );
				if ( im && im.src.startsWith( 'blob:' ) ) {
					URL.revokeObjectURL( im.src );
				}
				wrap.remove();
			} );
		const list = input.files ? Array.from( input.files ) : [];
		list.forEach( ( file, index ) => {
			if ( ! file.type.startsWith( 'image/' ) ) {
				return;
			}
			const wrap = document.createElement( 'div' );
			wrap.className = 'image-wrapper levcharity-pending-additional';
			wrap.dataset.pendingIndex = String( index );
			const img = document.createElement( 'img' );
			img.src = URL.createObjectURL( file );
			img.alt = '';
			img.style.width = '100px';
			img.style.height = '100px';
			img.style.borderRadius = '4px';
			img.style.objectFit = 'cover';
			img.style.marginRight = '10px';
			const del = document.createElement( 'div' );
			del.className = 'delete-btn remove-additional-image-button';
			del.textContent = 'X';
			wrap.appendChild( img );
			wrap.appendChild( del );
			container.appendChild( wrap );
		} );
	}

	#mergeAdditionalImageFiles( input, newFiles ) {
		const previous =
			TeamCampaignEdit.#pendingAdditionalFiles.get( input ) || [];
		const merged = [ ...previous, ...newFiles ];
		const seen = new Set();
		const unique = merged.filter( ( file ) => {
			const key = `${ file.name }|${ file.size }|${ file.lastModified }`;
			if ( seen.has( key ) ) {
				return false;
			}
			seen.add( key );
			return true;
		} );
		const dt = new DataTransfer();
		unique.forEach( ( file ) => dt.items.add( file ) );
		input.files = dt.files;
		TeamCampaignEdit.#pendingAdditionalFiles.set( input, unique );
		return unique;
	}

	#parseRemovedImageIds( value ) {
		if ( ! value ) {
			return [];
		}
		try {
			const parsed = JSON.parse( value );
			if ( Array.isArray( parsed ) ) {
				return parsed.map( String );
			}
		} catch {
			// Legacy comma-separated values.
		}
		return String( value )
			.split( ',' )
			.map( ( id ) => id.trim() )
			.filter( Boolean );
	}

	#appendRemovedImageId( removedInput, id ) {
		const ids = this.#parseRemovedImageIds( removedInput.value );
		const next = String( id );
		if ( ! ids.includes( next ) ) {
			ids.push( next );
		}
		removedInput.value = JSON.stringify( ids );
	}

	#getExistingAdditionalImageIds( root ) {
		const ids = [];
		root
			.querySelectorAll(
				'.levcharity-additional-images-preview-host .image-wrapper:not(.levcharity-pending-additional)'
			)
			.forEach( ( wrapper ) => {
				const fromWrapper = String( wrapper.dataset.id || '' ).trim();
				const fromImg = String(
					wrapper.querySelector( 'img[data-id]' )?.dataset.id || ''
				).trim();
				const fromHidden = String(
					wrapper.querySelector( 'input[name="existing_images[]"]' )
						?.value || ''
				).trim();
				const id = fromWrapper || fromImg || fromHidden;
				if ( id ) {
					ids.push( id );
				}
			} );
		return ids;
	}

	#syncGalleryImageIdsField( root ) {
		const galleryIdsInput = root.querySelector(
			'input[name="gallery_image_ids"]'
		);
		if ( ! galleryIdsInput ) {
			return;
		}
		galleryIdsInput.value = this.#getExistingAdditionalImageIds( root ).join(
			','
		);
	}

	#applySavedAdditionalImages( root, images ) {
		const container = root.querySelector(
			'.levcharity-additional-images-preview-host'
		);
		if ( ! container ) {
			return;
		}
		container
			.querySelectorAll( '.levcharity-pending-additional' )
			.forEach( ( wrap ) => {
				const im = wrap.querySelector( 'img' );
				if ( im && im.src.startsWith( 'blob:' ) ) {
					URL.revokeObjectURL( im.src );
				}
				wrap.remove();
			} );
		container
			.querySelectorAll( '.image-wrapper:not(.levcharity-pending-additional)' )
			.forEach( ( wrap ) => wrap.remove() );
		images.forEach( ( image ) => {
			const id = String( image.id ?? '' );
			const url = String( image.url ?? '' );
			if ( ! id || ! url ) {
				return;
			}
			const wrap = document.createElement( 'div' );
			wrap.className = 'image-wrapper';
			wrap.style.position = 'relative';
			wrap.dataset.id = id;
			const img = document.createElement( 'img' );
			img.src = url;
			img.alt = '';
			img.style.width = '100px';
			img.style.height = '100px';
			img.style.borderRadius = '4px';
			img.style.objectFit = 'cover';
			img.style.marginRight = '10px';
			const del = document.createElement( 'div' );
			del.className = 'delete-btn remove-additional-image-button';
			del.textContent = 'X';
			const hidden = document.createElement( 'input' );
			hidden.type = 'hidden';
			hidden.name = 'existing_images[]';
			hidden.value = id;
			wrap.appendChild( img );
			wrap.appendChild( del );
			wrap.appendChild( hidden );
			container.appendChild( wrap );
		} );
	}

	#bindAdditionalImagesPreview( root ) {
		const input = this.#getAdditionalCampaignImagesInput( root );
		if ( ! input ) {
			return;
		}
		input.addEventListener( 'change', () => {
			const picked = input.files ? Array.from( input.files ) : [];
			if ( picked.length ) {
				this.#mergeAdditionalImageFiles( input, picked );
			}
			this.#renderPendingAdditionalPreviews( root, input );
			const formEl = input.closest( 'form' );
			if ( formEl ) {
				formEl.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			}
		} );
	}

	#bindRemoveAdditional( root ) {
		root.addEventListener( 'click', ( e ) => {
			const del = e.target.closest( '.remove-additional-image-button' );
			if ( ! del || ! root.contains( del ) ) {
				return;
			}
			const wrapper = del.closest( '.image-wrapper' );
			if ( ! wrapper ) {
				return;
			}
			if ( wrapper.classList.contains( 'levcharity-pending-additional' ) ) {
				e.preventDefault();
				const idx = parseInt( wrapper.dataset.pendingIndex, 10 );
				const input = this.#getAdditionalCampaignImagesInput( root );
				if ( ! input || Number.isNaN( idx ) ) {
					return;
				}
				const remaining = Array.from( input.files ).filter(
					( _f, i ) => i !== idx
				);
				const dt = new DataTransfer();
				remaining.forEach( ( file ) => dt.items.add( file ) );
				input.files = dt.files;
				TeamCampaignEdit.#pendingAdditionalFiles.set( input, remaining );
				this.#renderPendingAdditionalPreviews( root, input );
				const formEl = input.closest( 'form' );
				if ( formEl ) {
					formEl.dispatchEvent( new Event( 'change', { bubbles: true } ) );
				}
				return;
			}
			e.preventDefault();
			const id =
				wrapper.dataset.id ||
				wrapper.querySelector( 'img[data-id]' )?.dataset.id ||
				wrapper.querySelector( 'input[name="existing_images[]"]' )?.value ||
				'';
			const removedInput = root.querySelector( '#removedImagesInput' );
			if ( removedInput && id ) {
				this.#appendRemovedImageId( removedInput, id );
			}
			wrapper.remove();
			this.#syncGalleryImageIdsField( root );
			const formEl = removedInput?.closest( 'form' ) || wrapper.closest( 'form' );
			if ( formEl ) {
				formEl.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			}
		} );
	}

	static #tShirtBlockSelector =
		'.levcharity-team-campaign-tshirt, .chaiathon_t_shirt_size, .gamification_t_shirt_size';

	static syncTShirtFieldVisibility( root ) {
		const block = root?.querySelector( TeamCampaignEdit.#tShirtBlockSelector );
		if ( ! block ) {
			return false;
		}
		const select = block.querySelector( 'select[name="t_shirt_size"]' );
		const teamId = String(
			root.querySelector( 'input[name="team_id"]' )?.value || ''
		);
		const display = String( block.dataset.display || '' );
		let visible = block.style.display !== 'none' && block.offsetParent !== null;

		if ( display ) {
			if ( display === '1' ) {
				visible = true;
			} else if ( teamId ) {
				visible = display
					.split( ',' )
					.map( ( id ) => id.trim() )
					.filter( Boolean )
					.includes( teamId );
			} else {
				visible = false;
			}
			block.style.display = visible ? '' : 'none';
		}

		if ( select ) {
			if ( visible ) {
				select.setAttribute( 'required', 'required' );
			} else {
				select.removeAttribute( 'required' );
			}
		}

		return visible;
	}

	static releaseHiddenTShirtRequired( form ) {
		if ( ! form ) {
			return;
		}
		form.querySelectorAll( TeamCampaignEdit.#tShirtBlockSelector ).forEach( ( block ) => {
			const select = block.querySelector( 'select[name="t_shirt_size"]' );
			if ( ! select ) {
				return;
			}
			const hidden =
				block.style.display === 'none' || block.offsetParent === null;
			if ( hidden ) {
				select.removeAttribute( 'required' );
			}
		} );
	}

	#applyTshirtVisibility( root ) {
		TeamCampaignEdit.syncTShirtFieldVisibility( root );
	}

	async #verifyEditContext( root ) {
		const action = root.dataset.action;
		const token = root.dataset.token;
		const metaId = root.dataset.metaId;
		const ajaxUrl =
			typeof window !== 'undefined' &&
			window.admin_ajax &&
			window.admin_ajax[ 0 ]
				? window.admin_ajax[ 0 ]
				: '';
		if ( ! action || ! token || ! metaId || ! ajaxUrl ) {
			return;
		}
		const verifyError =
			typeof wp !== 'undefined' && wp.i18n
				? wp.i18n.__(
						'You cannot edit this campaign or your session expired.',
						'levcharity'
				  )
				: 'You cannot edit this campaign or your session expired.';
		const res = await TeamCampaignEdit.requestWithToast(
			ajaxUrl,
			{ action, token, metaId },
			{ errorMessage: verifyError }
		);
		if ( ! res ) {
			return;
		}
		const block = root.querySelector( TeamCampaignEdit.#tShirtBlockSelector );
		if ( block && String( res.display_t_shirt_for_teams || '' ) === '1' ) {
			block.dataset.display = '1';
		}
		TeamCampaignEdit.syncTShirtFieldVisibility( root );
	}
}
